import { formatCurrencyWithCommas, formatPercentage, roundToDecimals } from "../../../utils/numberUtils";

export function formatSignedPct(value: number): string {
  if (!Number.isFinite(value)) return "--";
  const rounded = roundToDecimals(value, 2);
  const abs = formatPercentage(Math.abs(rounded));
  return rounded < 0 ? `-${abs}` : abs;
}

export function formatSignedDollars(value: number): string {
  if (!Number.isFinite(value)) return "--";
  const rounded = roundToDecimals(value, 0);
  const abs = formatCurrencyWithCommas(Math.abs(rounded), 0);
  return rounded < 0 ? `-${abs}` : abs;
}

type StrategyAppearance = "default" | "alpha";

export function capacityToneClass(value: number, appearance: StrategyAppearance = "default"): string {
  const isAlpha = appearance === "alpha";
  if (!Number.isFinite(value)) return isAlpha ? "text-v7-ink/40" : "text-gray-400 dark:text-gray-500";
  const rounded = roundToDecimals(value, 2);
  if (rounded < 0) return isAlpha ? "text-v7-rust" : "text-red-600 dark:text-red-500";
  if (rounded === 0) return isAlpha ? "text-v7-ink-soft" : "text-gray-500 dark:text-gray-400";
  return isAlpha ? "text-v7-success" : "text-green-600 dark:text-green-500";
}

export function AvailableCapacityPctCell({
  value,
  appearance = "default",
}: {
  value: number;
  appearance?: StrategyAppearance;
}) {
  const isAlpha = appearance === "alpha";
  const rounded = roundToDecimals(value, 2);
  const tone = capacityToneClass(rounded, appearance);
  const isOver = Number.isFinite(rounded) && rounded < 0;
  const isFull = Number.isFinite(rounded) && rounded === 0;
  return (
    <td className={`px-4 py-2.5 text-right font-mono tabular-nums text-sm ${tone}`}>
      <span className="inline-flex items-center justify-end gap-1.5">
        <span>{formatSignedPct(rounded)}</span>
        {isOver && (
          <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border ${isAlpha
            ? "rounded-v7-control border-v7-rust/30 bg-v7-rust/10 text-v7-rust"
            : "rounded border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-400"}`}>
            Over
          </span>
        )}
        {isFull && (
          <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border ${isAlpha
            ? "rounded-v7-control border-v7-ink/15 bg-v7-paper text-v7-ink-soft"
            : "rounded border-gray-200 bg-gray-100 text-gray-600 dark:border-gray-800 dark:bg-rp-surface-raised dark:text-gray-400"}`}>
            Full
          </span>
        )}
      </span>
    </td>
  );
}

export function AvailableCapacityDollarsCell({
  value,
  appearance = "default",
}: {
  value: number | null;
  appearance?: StrategyAppearance;
}) {
  const isAlpha = appearance === "alpha";
  if (value == null) {
    return <td className={`px-4 py-2.5 text-right font-mono tabular-nums text-sm ${isAlpha ? "text-v7-ink/40" : "text-gray-400 dark:text-gray-500"}`}>--</td>;
  }
  const rounded = roundToDecimals(value, 0);
  const tone = capacityToneClass(rounded, appearance);
  return (
    <td className={`px-4 py-2.5 text-right font-mono tabular-nums text-sm ${tone}`}>
      {formatSignedDollars(rounded)}
    </td>
  );
}
